import type { DensitySnapshot, WrappedCoin } from "@screener/protocol";
import { memo, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { noteIndicatorFor } from "./coin-note-indicator";
import { samePinMatch } from "./density-card-row";
import { DensityRow, type FavoriteIndicator, type NoteIndicator } from "./density-row";
import type { RowPinMatch } from "./pin-match";

/**
 * Мемоизированная строка таблицы, «По монете» и раскрытой группы «Ситуаций»
 * (design.md stage9-render-scope, решение 3). Колбэки приходят от родителя
 * постоянными ссылками и адресуются строкой или ключом; замыкания для
 * `DensityRow` строит сама обёртка — интерфейс строки не меняется. Поэтому
 * срез одной плотности перерисовывает одну строку, а не все.
 */
export interface DensityRowItemProps {
  row: DensitySnapshot;
  wrapped: readonly WrappedCoin[];
  stale: boolean;
  focusable: boolean;
  hasNote: NoteIndicator;
  /** Совпадение закрепления: объекты пересобираются на каждый кадр, сравниваются по значению. */
  pinMatch: RowPinMatch | "unknown" | undefined;
  pinPending: boolean;
  favoriteMatch: FavoriteIndicator;
  favoritePending: boolean;
  onFocus: (densityId: string) => void;
  onKeyDown: (densityId: string, event: ReactKeyboardEvent<HTMLTableRowElement>) => void;
  onOpenRow: (row: DensitySnapshot) => void;
  onOpenNotes: (row: DensitySnapshot) => void;
  onTogglePin: (row: DensitySnapshot) => void;
  onToggleFavorite: (row: DensitySnapshot) => void;
  onHideCoin: (base: string) => void;
  onCopyTicker: (row: DensitySnapshot) => void;
}

/** Сопоставления клиента, которыми список снабжает каждую свою строку. */
export interface RowAnnotationSources {
  /** `null` — список заметок не прочитан: строка не вправе утверждать «заметок нет». */
  notedBases: Set<string> | null;
  /** `null` — список закреплений не прочитан. */
  pinMatches: Map<string, RowPinMatch> | null;
  pendingPinIds: ReadonlySet<string>;
  /** `null` — список избранного не прочитан. */
  favoriteBases: Set<string> | null;
  pendingFavoriteBases: ReadonlySet<string>;
}

/** Примитивные пропсы строки, выведенные из сопоставлений списка: заметка, закрепление, избранное. */
export function rowAnnotations(
  row: DensitySnapshot,
  sources: RowAnnotationSources,
): Pick<
  DensityRowItemProps,
  "hasNote" | "pinMatch" | "pinPending" | "favoriteMatch" | "favoritePending"
> {
  const { base } = row.instrument;
  return {
    hasNote: noteIndicatorFor(base, sources.notedBases),
    pinMatch: sources.pinMatches === null ? "unknown" : sources.pinMatches.get(row.density_id),
    pinPending: sources.pendingPinIds.has(row.density_id),
    favoriteMatch:
      sources.favoriteBases === null ? "unknown" : sources.favoriteBases.has(base) ? "yes" : "no",
    favoritePending: sources.pendingFavoriteBases.has(base),
  };
}

/** Сколько пропсов знает `areDensityRowItemPropsEqual`: лишний ключ не даёт строке застыть. */
const KNOWN_PROP_COUNT = 17;

/**
 * Строки равны, когда равны все пропсы по ссылке или значению, а совпадение
 * закрепления — по `samePinMatch`. Незнакомое поле в пропсах, которого эта
 * функция не знает, считается отличием: строка перерисуется, но не застынет.
 */
export function areDensityRowItemPropsEqual(
  previous: DensityRowItemProps,
  next: DensityRowItemProps,
): boolean {
  return (
    Object.keys(previous).length === KNOWN_PROP_COUNT &&
    Object.keys(next).length === KNOWN_PROP_COUNT &&
    previous.row === next.row &&
    previous.wrapped === next.wrapped &&
    previous.stale === next.stale &&
    previous.focusable === next.focusable &&
    previous.hasNote === next.hasNote &&
    samePinMatch(previous.pinMatch, next.pinMatch) &&
    previous.pinPending === next.pinPending &&
    previous.favoriteMatch === next.favoriteMatch &&
    previous.favoritePending === next.favoritePending &&
    previous.onFocus === next.onFocus &&
    previous.onKeyDown === next.onKeyDown &&
    previous.onOpenRow === next.onOpenRow &&
    previous.onOpenNotes === next.onOpenNotes &&
    previous.onTogglePin === next.onTogglePin &&
    previous.onToggleFavorite === next.onToggleFavorite &&
    previous.onHideCoin === next.onHideCoin &&
    previous.onCopyTicker === next.onCopyTicker
  );
}

function DensityRowItemView({
  row,
  wrapped,
  stale,
  focusable,
  hasNote,
  pinMatch,
  pinPending,
  favoriteMatch,
  favoritePending,
  onFocus,
  onKeyDown,
  onOpenRow,
  onOpenNotes,
  onTogglePin,
  onToggleFavorite,
  onHideCoin,
  onCopyTicker,
}: DensityRowItemProps) {
  return (
    <DensityRow
      row={row}
      wrapped={wrapped}
      stale={stale}
      focusable={focusable}
      onFocus={() => onFocus(row.density_id)}
      onKeyDown={(event) => onKeyDown(row.density_id, event)}
      onOpen={() => onOpenRow(row)}
      hasNote={hasNote}
      pinMatch={pinMatch}
      onOpenNotes={() => onOpenNotes(row)}
      onTogglePin={() => onTogglePin(row)}
      pinPending={pinPending}
      favoriteMatch={favoriteMatch}
      onToggleFavorite={() => onToggleFavorite(row)}
      favoritePending={favoritePending}
      onHideCoin={() => onHideCoin(row.instrument.base)}
      onCopyTicker={() => onCopyTicker(row)}
    />
  );
}

export const DensityRowItem = memo(DensityRowItemView, areDensityRowItemPropsEqual);
